import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "@corti/dictation-web";
import { axiosPrescription } from "../../../../api/axios";
import "./ReasonAndHistorySection.css";

const reasons = [
  "ckd", "aki", "edema", "edematous", "nephritic", "rpgn", "hypertension", "urgency",
];
const knownReasons = [...reasons, "emergency"];

export default function ReasonAndHistorySection({
  patientId,
  historyRecords = [],
  historyLoading = false,
  historyError,
}) {
  const { t, i18n } = useTranslation("neurologue");
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [customReasons, setCustomReasons] = useState("");
  const [addedCustomReasons, setAddedCustomReasons] = useState([]);
  const [patientHistory, setPatientHistory] = useState("");
  const [examDictation, setExamDictation] = useState(false);
  const [dictationStatus, setDictationStatus] = useState("idle");
  const [dictationError, setDictationError] = useState("");
  const dictationRef = useRef(null);
  const dictationAbortRef = useRef(null);
  const dictationOperationRef = useRef(false);
  const dictationStopRequestedRef = useRef(false);
  const transcriptSegmentsRef = useRef(new Set());
  const dictationLanguage = String(i18n.resolvedLanguage || i18n.language || "fr")
    .toLowerCase()
    .startsWith("en") ? "en" : "fr";
  const savedCustomReasons = (Array.isArray(historyRecords) ? historyRecords : [])
    .flatMap((record) => {
      const value = record?.diseaseHistory;
      if (Array.isArray(value)) return value;
      const text = String(value || "");
      return text.includes("\n") ? text.split("\n") : text.split(",");
    })
    .map((reason) => String(reason).trim())
    .filter((reason) =>
      reason &&
      !knownReasons.includes(reason) &&
      !knownReasons.some((key) => key.toLowerCase() === reason.toLowerCase()) &&
      !knownReasons.some((key) => t(`reason.reasons.${key}`).trim().toLowerCase() === reason.toLowerCase()),
    );
  const customReasonOptions = [...new Map(
    [...savedCustomReasons, ...addedCustomReasons].map((reason) => [reason.toLowerCase(), reason]),
  ).values()];
  const draftReason = customReasons.trim();
  const visibleCustomReasons = draftReason &&
    !customReasonOptions.some((reason) => reason.toLowerCase() === draftReason.toLowerCase()) &&
    !knownReasons.some((key) => t(`reason.reasons.${key}`).trim().toLowerCase() === draftReason.toLowerCase())
    ? [...customReasonOptions, draftReason]
    : customReasonOptions;

  useEffect(() => {
    setSelectedReasons([]);
    setCustomReasons("");
    setAddedCustomReasons([]);
    setPatientHistory("");
    setExamDictation(false);
  }, [patientId]);

  const stopDictation = useCallback(async () => {
    dictationStopRequestedRef.current = true;
    dictationAbortRef.current?.abort();
    dictationAbortRef.current = null;
    const dictation = dictationRef.current;
    try {
      dictation?.stopRecording?.();
      await dictation?.closeConnection?.();
    } catch (error) {
      console.error("Failed to close consultation dictation:", error);
    }
    setDictationStatus("idle");
  }, []);

  useEffect(() => {
    void stopDictation();
  }, [patientId, stopDictation]);

  const startDictation = async () => {
    if (dictationOperationRef.current || dictationStatus === "recording") return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setDictationError(t("reason.dictationUnsupported"));
      setDictationStatus("error");
      return;
    }

    const dictation = dictationRef.current;
    if (!dictation) {
      setDictationError(t("reason.dictationUnavailable"));
      setDictationStatus("error");
      return;
    }

    dictationOperationRef.current = true;
    dictationStopRequestedRef.current = false;
    transcriptSegmentsRef.current.clear();
    setDictationError("");
    setDictationStatus("connecting");
    const controller = new AbortController();
    dictationAbortRef.current = controller;

    try {
      const response = await axiosPrescription.post(
        "/prescriptions/corti/browser-token",
        { language: dictationLanguage },
        { signal: controller.signal },
      );
      if (dictationStopRequestedRef.current) return;
      const token = response?.data?.token;
      if (!token) throw new Error(t("reason.dictationTokenMissing"));

      dictation.accessToken = token;
      dictation.settingsEnabled = [];
      dictation.allowButtonFocus = true;
      dictation.pushToTalkKeybinding = undefined;
      dictation.toggleToTalkKeybinding = undefined;
      dictation.dictationConfig = {
        automaticPunctuation: true,
        interimResults: true,
        primaryLanguage: dictationLanguage,
        spokenPunctuation: true,
      };
      dictation.startRecording?.();
      setDictationStatus("recording");
    } catch (error) {
      if (error?.name === "AbortError") {
        setDictationStatus("idle");
      } else {
        console.error("Failed to start consultation dictation:", error);
        const message = error?.response?.data?.message || error?.message;
        setDictationError(
          error?.name === "NotAllowedError"
            ? t("reason.dictationPermissionDenied")
            : String(message || t("reason.dictationStartFailed")),
        );
        setDictationStatus("error");
      }
    } finally {
      dictationOperationRef.current = false;
      dictationAbortRef.current = null;
    }
  };

  const toggleDictation = () => {
    if (dictationStatus === "recording" || dictationStatus === "connecting") {
      void stopDictation();
    } else {
      void startDictation();
    }
  };

  useEffect(() => {
    const dictation = dictationRef.current;
    if (!dictation) return undefined;

    const handleTranscript = (event) => {
      const detail = event?.detail?.data || event?.detail || {};
      const transcript = String(detail.text || "").trim();
      if (!transcript || !detail.isFinal) return;
      const segmentKey = `${transcript}|${detail.start ?? ""}|${detail.end ?? ""}`;
      if (transcriptSegmentsRef.current.has(segmentKey)) return;
      transcriptSegmentsRef.current.add(segmentKey);
      setPatientHistory((current) =>
        `${current}${current && !/\s$/.test(current) ? " " : ""}${transcript}`,
      );
    };
    const handleRecordingState = (event) => {
      if (event?.detail?.state === "stopped") setDictationStatus("idle");
      if (event?.detail?.state === "recording") setDictationStatus("recording");
    };
    const handleDictationError = (event) => {
      const error = event?.detail || event;
      console.error("Consultation dictation error:", error);
      const message = error?.message || error?.detail?.message;
      setDictationError(String(message || t("reason.dictationStartFailed")));
      setDictationStatus("error");
    };

    dictation.addEventListener("transcript", handleTranscript);
    dictation.addEventListener("recording-state-changed", handleRecordingState);
    dictation.addEventListener("error", handleDictationError);
    return () => {
      dictation.removeEventListener("transcript", handleTranscript);
      dictation.removeEventListener("recording-state-changed", handleRecordingState);
      dictation.removeEventListener("error", handleDictationError);
    };
  }, [t]);

  useEffect(() => {
    const stopWhenHidden = () => {
      if (document.hidden) void stopDictation();
    };
    document.addEventListener("visibilitychange", stopWhenHidden);
    return () => document.removeEventListener("visibilitychange", stopWhenHidden);
  }, [stopDictation]);

  useEffect(() => () => {
    dictationAbortRef.current?.abort();
    dictationRef.current?.stopRecording?.();
    const closing = dictationRef.current?.closeConnection?.();
    closing?.catch((error) => {
      console.error("Failed to close consultation dictation:", error);
    });
  }, []);

  const toggleReason = (reason) => {
    setSelectedReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason]
    );
  };

  const selectCustomReason = (reason) => {
    setAddedCustomReasons((current) => current.some((item) => item.toLowerCase() === reason.toLowerCase())
      ? current
      : [...current, reason]);
    toggleReason(reason);
    if (draftReason.toLowerCase() === reason.toLowerCase()) setCustomReasons("");
  };

  const clearSection = (event) => {
    event.currentTarget.form.reset();
    void stopDictation();
    setSelectedReasons([]);
    setCustomReasons("");
    setAddedCustomReasons([]);
    setPatientHistory("");
    setExamDictation(false);
  };

  return (
    <form className="reason-history" onSubmit={(event) => event.preventDefault()}>
      <div className="reason-history__field">
        <label className="reason-history__label">{t("reason.title")}</label>
        <div className="reason-history__chips">
        {reasons.map((reason) => (
            <button
              type="button"
              className={`reason-history__chip${selectedReasons.includes(reason) ? " is-selected" : ""}`}
              key={reason}
              aria-pressed={selectedReasons.includes(reason)}
              data-reason={reason}
              onClick={() => toggleReason(reason)}
            >
              {t(`reason.reasons.${reason}`)}
            </button>
        ))}
        {visibleCustomReasons.map((reason) => (
          <button
            type="button"
            className={`reason-history__chip${selectedReasons.includes(reason) ? " is-selected" : ""}`}
            key={`custom-${reason}`}
            aria-pressed={selectedReasons.includes(reason)}
            data-reason={reason}
            data-custom-reason="true"
            onClick={() => selectCustomReason(reason)}
          >
            {reason}
          </button>
        ))}
        </div>
        <input
          className="reason-history__input"
          type="text"
          placeholder={t("reason.addReason")}
          value={customReasons}
          onChange={(event) => setCustomReasons(event.target.value)}
        />
      </div>
      <div className="reason-history__field">
        <div className="reason-history__label-row">
          <label className="reason-history__label" htmlFor="patient-exam">{t("reason.patientHistory")}</label>
          <div className="reason-history__dictation">
            <span>{t("reason.autoDictate")}</span>
            <button
              type="button"
              className={`reason-history__switch${examDictation ? " is-on" : ""}`}
              role="switch"
              aria-checked={examDictation}
              aria-label={t("reason.toggleExamDictation")}
              onClick={() => setExamDictation((enabled) => !enabled)}
            ><span /></button>
            <button
              type="button"
              className={`reason-history__mic${dictationStatus === "recording" ? " is-recording" : ""}`}
              aria-label={dictationStatus === "recording" || dictationStatus === "connecting" ? t("reason.stopExamDictation") : t("reason.startExamDictation")}
              aria-pressed={dictationStatus === "recording" || dictationStatus === "connecting"}
              onClick={toggleDictation}
            >
              {dictationStatus === "connecting"
                ? t("reason.dictationConnecting")
                : dictationStatus === "recording"
                  ? (
                    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                      <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor" />
                    </svg>
                  )
                  : (
                    <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
                      <rect x="6" y="2" width="4" height="8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M4 7.5a4 4 0 0 0 8 0M8 12v2m-2 0h4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  )}
            </button>
          </div>
        </div>
        <textarea
          id="patient-exam"
          className="reason-history__textarea"
          placeholder={t("reason.examPlaceholder")}
          value={patientHistory}
          onChange={(event) => setPatientHistory(event.target.value)}
        />
      </div>

      {dictationError && <p className="reason-history__dictation-error" role="alert">{dictationError}</p>}
      {historyLoading && <p role="status">{t("common.loading", { defaultValue: "Chargement de l’historique…" })}</p>}
      {historyError && <p role="alert">{String(historyError)}</p>}
      <button type="button" className="reason-history__clear" onClick={clearSection}>{t("reason.clear")}</button>
      <corti-dictation ref={dictationRef} aria-hidden="true" className="reason-history__hidden-dictation" />
    </form>
  );
}
